import { type FetchPolicy } from '@apollo/client';
import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@fhs/client';
import {
  type IUserAccountsQuery,
  UserAccountsDocument,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client } from '@fhs-legacy/frontend/src/state/graphql/client';
import { useLocale } from '@fhs-legacy/frontend/src/state/intl';

type UserAccountsData = NonNullable<IUserAccountsQuery['userAccounts']>;

const userAccountsQueryKey = (feCountryCode: string) => ['userAccounts', feCountryCode] as const;

async function fetchUserAccounts(feCountryCode: string, fetchPolicy: FetchPolicy) {
  const { data } = await client.query({
    query: UserAccountsDocument,
    variables: { feCountryCode },
    fetchPolicy,
  });
  return data.userAccounts as UserAccountsData;
}

export async function fetchUserAccountsFromNetwork(feCountryCode: string) {
  return fetchUserAccounts(feCountryCode, 'network-only');
}

export { userAccountsQueryKey };

export const useUserAccountsQuery = () => {
  const { feCountryCode } = useLocale();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: userAccountsQueryKey(feCountryCode),
    queryFn: () => fetchUserAccounts(feCountryCode, 'cache-first'),
  });

  const refetch = useCallback(async () => {
    const userAccounts = await fetchUserAccounts(feCountryCode, 'network-only');
    queryClient.setQueryData(userAccountsQueryKey(feCountryCode), userAccounts);
    return { data: userAccounts };
  }, [feCountryCode, queryClient]);

  return {
    ...query,
    refetch,
  };
};
